import type { LayerStyleSlice, GeoDataStateCreator, LayerStyleProperties, LayerBase, GeoDataState } from './types'; // Importa GeoDataState
import { selectAllAppLayers } from './layerSlice'; // Importa il selettore per accedere ai layer

// Definizioni degli stili di default per tipo di layer
const defaultLayerStylesByType: Record<LayerBase['maplibreLayerType'], Partial<LayerStyleProperties>> = {
  fill: {
    fillColor: '#667799', // Grigio Bluastro
    fillOpacity: 0.6,
    fillAntialias: true,
    fillTranslate: [0, 0],
    fillTranslateAnchor: 'map',
    fillPattern: null,
    fillSortKey: null,
    // Aggiungiamo anche le proprietà di linea per il bordo (outline)
    lineColor: '#444444', // Grigio Scuro (per fill-outline-color)
    lineWidth: 1,       // (per fill-outline-width, anche se non esiste direttamente)
    lineOpacity: 1.0,   // (per fill-outline-opacity, anche se non esiste direttamente)
  },
  line: {
    lineColor: '#444444', // Grigio Scuro
    lineWidth: 1,
    lineOpacity: 1.0,
    lineCap: 'butt',
    lineJoin: 'miter',
    lineMiterLimit: 2,
    lineRoundLimit: 1.05,
    lineSortKey: null,
    lineTranslate: [0, 0],
    lineTranslateAnchor: 'map',
    lineGapWidth: 0,
    lineOffset: 0,
    lineBlur: 0,
    lineDasharray: null,
    linePattern: null,
  },
  circle: {
    circleColor: '#667799', // Grigio Bluastro
    circleRadius: 5,
    circleOpacity: 1.0,
    circleStrokeColor: '#444444', // Grigio Scuro
    circleStrokeWidth: 1,
    circleBlur: 0,
    circleTranslate: [0, 0],
    circleTranslateAnchor: 'map',
    circlePitchScale: 'map',
    circlePitchAlignment: 'viewport', // Default corretto
    circleStrokeOpacity: 1.0,
    circleSortKey: null,
  },
  symbol: {
    // Layout
    symbolPlacement: 'point',
    symbolSpacing: 250,
    symbolAvoidEdges: false,
    symbolSortKey: null,
    symbolZOrder: 'auto',
    iconAllowOverlap: false,
    iconIgnorePlacement: false,
    iconOptional: false,
    iconRotationAlignment: 'auto',
    iconSize: 1,
    iconTextFit: 'none',
    iconTextFitPadding: [0, 0, 0, 0],
    iconImage: null,
    iconRotate: 0,
    iconPadding: 2,
    iconKeepUpright: false,
    iconOffset: [0, 0],
    iconAnchor: 'center',
    iconPitchAlignment: 'auto',
    textPitchAlignment: 'auto',
    textRotationAlignment: 'auto',
    textField: null,
    textFont: ['Open Sans Regular', 'Arial Unicode MS Regular'],
    textSize: 16,
    textMaxWidth: 10,
    textLineHeight: 1.2,
    textLetterSpacing: 0,
    textJustify: 'center',
    textRadialOffset: 0,
    textVariableAnchor: null,
    textAnchor: 'center',
    textMaxAngle: 45,
    textRotate: 0,
    textPadding: 2,
    textKeepUpright: true,
    textTransform: 'none',
    textOffset: [0, 0],
    textAllowOverlap: false,
    textIgnorePlacement: false,
    textOptional: false,
    textWritingMode: null,
    // Paint
    iconOpacity: 1,
    iconColor: '#000000',
    iconHaloColor: 'rgba(0, 0, 0, 0)',
    iconHaloWidth: 0,
    iconHaloBlur: 0,
    iconTranslate: [0, 0],
    iconTranslateAnchor: 'map',
    textOpacity: 1,
    textColor: '#000000',
    textHaloColor: 'rgba(0, 0, 0, 0)',
    textHaloWidth: 0,
    textHaloBlur: 0,
    textTranslate: [0, 0],
    textTranslateAnchor: 'map',
  },
  'fill-extrusion': {
    // Layout
    // Paint
    fillExtrusionOpacity: 1,
    fillExtrusionColor: '#000000',
    fillExtrusionTranslate: [0, 0],
    fillExtrusionTranslateAnchor: 'map',
    fillExtrusionPattern: null,
    fillExtrusionHeight: 10,
    fillExtrusionBase: 0,
    fillExtrusionVerticalGradient: true,
  },
  heatmap: {
    // Layout
    visibility: 'visible',
    // Paint
    heatmapColor: [
      "interpolate",
      ["linear"],
      ["heatmap-density"],
      0, "rgba(0, 0, 255, 0)",
      0.1, "royalblue",
      0.3, "cyan",
      0.5, "lime",
      0.7, "yellow",
      1, "red"
    ],
    heatmapIntensity: 1,
    heatmapOpacity: 1,
    heatmapRadius: 30,
    heatmapWeight: 1,
  },
  raster: {
    rasterOpacity: 1.0,
  },
  wms: { // wms condivide gli stili di raster per ora
    rasterOpacity: 1.0,
  },
  background: {
    backgroundColor: '#f0f0f0', // Un grigio chiaro di default
    backgroundPattern: null,
    backgroundOpacity: 1.0,
  },
  // Aggiungere qui altri tipi di layer se necessario
};

// Default comuni per popup (applicabili a tutti i tipi potenzialmente)
const commonPopupDefaults = {
  isPopupEnabled: false,
  popupFields: null, // null significa "mostra tutti i campi"
};

// Funzione helper per ottenere gli stili di default basati sul tipo di layer
const getDefaultStyles = (maplibreLayerType: LayerBase['maplibreLayerType']): LayerStyleProperties => {
  const typeSpecificDefaults = defaultLayerStylesByType[maplibreLayerType];

  if (typeSpecificDefaults) {
    return {
      ...commonPopupDefaults,
      ...typeSpecificDefaults,
    } as LayerStyleProperties; // Cast necessario perché Partial<LayerStyleProperties> + common
  }

  // Ritorna solo i default comuni se il tipo non è riconosciuto
  // o non ha stili di default specifici
  console.warn(`[getDefaultStyles] Tipo layer non riconosciuto o senza stili specifici: ${maplibreLayerType}. Applicati solo default comuni per popup.`);
  return { ...commonPopupDefaults } as LayerStyleProperties; // Cast a LayerStyleProperties
};


export const createLayerStyleSlice: GeoDataStateCreator<LayerStyleSlice> = (set) => ({
  layerStyles: {},
  setLayerStyleDefaults: (layerId, maplibreLayerType) => {
    const defaultStyles = getDefaultStyles(maplibreLayerType);
    set((state) => ({
      layerStyles: {
        ...state.layerStyles,
        [layerId]: defaultStyles,
      },
    }));
  },
  updateLayerStyleProperty: (layerId, propertyName, value) =>
    set((state) => {
      // Verifica se il layer esiste prima di tentare l'aggiornamento
      if (!state.layerStyles[layerId]) {
        console.warn(`[updateLayerStyleProperty] Layer style with ID ${layerId} not found.`);
        return {}; // Non modificare lo stato se il layer non esiste
      }
      return {
        layerStyles: {
          ...state.layerStyles,
          [layerId]: {
            ...state.layerStyles[layerId],
            [propertyName]: value,
          },
        },
      };
    }),
  removeLayerStyles: (layerId) =>
    set((state) => {
      const newLayerStyles = { ...state.layerStyles };
      delete newLayerStyles[layerId];
      return { layerStyles: newLayerStyles };
    }),
  togglePopupEnabled: (layerId: string) =>
    set((state) => {
      if (!state.layerStyles[layerId]) {
        console.warn(`[togglePopupEnabled] Layer style with ID ${layerId} not found.`);
        return {};
      }
      return {
        layerStyles: {
          ...state.layerStyles,
          [layerId]: {
            ...state.layerStyles[layerId],
            isPopupEnabled: !state.layerStyles[layerId].isPopupEnabled,
          },
        },
      };
    }),
  setPopupFields: (layerId: string, fields: string[] | null) =>
    set((state) => {
      if (!state.layerStyles[layerId]) {
        console.warn(`[setPopupFields] Layer style with ID ${layerId} not found.`);
        return {};
      }
      return {
        layerStyles: {
          ...state.layerStyles,
          [layerId]: {
            ...state.layerStyles[layerId],
            popupFields: fields,
          },
        },
      };
    }),
  resetLayerStyleToDefault: (layerId: string) =>
    set((state) => {
      const targetLayer = selectAllAppLayers(state as GeoDataState).find(l => l.id === layerId); // Accedi allo stato completo per usare il selettore

      if (!targetLayer) {
        console.warn(`[resetLayerStyleToDefault] Layer with ID ${layerId} not found.`);
        return {};
      }

      if (!state.layerStyles[layerId]) {
        console.warn(`[resetLayerStyleToDefault] Layer style with ID ${layerId} not found, cannot reset.`);
        return {};
      }

      const defaultStyles = getDefaultStyles(targetLayer.maplibreLayerType);

      return {
        layerStyles: {
          ...state.layerStyles,
          [layerId]: defaultStyles,
        },
      };
    }),
});

// Selettore per ottenere tutti gli stili dei layer
export const selectAllLayerStyles = (state: GeoDataState): LayerStyleSlice['layerStyles'] => { // Accetta GeoDataState
  return state.layerStyles; // Accede direttamente
};